import { useEffect, useRef } from 'react';

export default function ConciergeLauncher({
  onClick,
  label = 'Chat with Concierge',
  hasUnread = false,
  variant = 'bubble',
  isOpen = false,
}) {
  const ariaLabel = isOpen ? 'Minimize chat' : label;
  const sideTabRef = useRef(null);

  // Track the visual viewport so the button stays glued to the actual visible
  // bottom-right corner even when the mobile browser chrome (address bar,
  // bottom nav) appears or disappears and changes the visual viewport height.
  useEffect(() => {
    if (variant !== 'side-tab') return;
    const vv = window.visualViewport;
    if (!vv || !sideTabRef.current) return;

    const update = () => {
      const offsetBottom = window.innerHeight - (vv.offsetTop + vv.height);
      sideTabRef.current.style.bottom = `${Math.max(0, offsetBottom)}px`;
    };

    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    update();

    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [variant]);

  if (variant === 'side-tab') {
    // bottom-0 right-0 anchors the element; the useEffect above adjusts `bottom`
    // via the Visual Viewport API so it tracks the actual visible bottom edge.
    // rounded-tl-full creates the quarter-circle shape via border-radius.
    // "AI" is absolutely positioned at the quarter-disk centroid (4r/3π from each edge).
    return (
      <button
        ref={sideTabRef}
        type="button"
        onClick={onClick}
        className="fixed bottom-0 right-0 z-40 h-[142px] w-[142px] rounded-tl-full bg-[#104A4F] text-white transition-colors hover:bg-[#155a5f] focus-visible:outline-none"
        aria-label={ariaLabel}
      >
        {hasUnread && (
          <span className="absolute left-8 top-8 h-2.5 w-2.5 rounded-full bg-emerald-400" />
        )}
        <span className="absolute left-[68px] top-[72px] text-[15px] font-semibold tracking-wide">
          AI
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2.5 rounded-full bg-slate-950 px-3 py-3 text-sm font-medium text-white shadow-[0_18px_40px_rgba(15,23,42,0.22)] transition-all hover:-translate-y-0.5 hover:bg-slate-900 hover:shadow-[0_22px_44px_rgba(15,23,42,0.26)] sm:bottom-6 sm:right-6 sm:px-3.5"
      aria-label={ariaLabel}
    >
      <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white">
        {isOpen ? (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m6 9 6 6 6-6" />
          </svg>
        ) : (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.8"
              d="M8 10h8M8 14h5m-7 6 3.6-3H18a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h.4L6 20Z"
            />
          </svg>
        )}
        {hasUnread && !isOpen && (
          <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-slate-950 bg-emerald-400" />
        )}
      </span>
      {!isOpen && (
        <span className="hidden text-[14px] font-medium tracking-[-0.01em] text-white sm:block">
          {label}
        </span>
      )}
    </button>
  );
}
